import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Activity,
  Users,
  UserCheck,
  UserX,
  AlertCircle,
  Clock,
  CheckCircle,
  MapPin,
  Loader2
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';

import api, { endpoints } from '@/services/api';
import TeamLocationModal from '@/components/TeamLocationModal';

// M-1, M-2, ..., M-30, T-1, T-2, ... şeklinde sayısal sırala
const sortTeamsByCode = (teams) => {
  return [...teams].sort((a, b) => {
    const parseCode = (code) => {
      if (!code) return { prefix: '', num: 9999 };
      const match = code.match(/^([A-Za-zÇĞİÖŞÜçğışöşü]+)-?(\d+)$/);
      if (match) return { prefix: match[1].toUpperCase(), num: parseInt(match[2], 10) };
      return { prefix: code.toUpperCase(), num: 0 };
    };
    const ca = parseCode(a.team_code || a.code);
    const cb = parseCode(b.team_code || b.code);
    if (ca.prefix !== cb.prefix) return ca.prefix.localeCompare(cb.prefix, 'tr');
    return ca.num - cb.num;
  });
};

const TeamStatusPage = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [locationData, setLocationData] = useState(null);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [locationLoadingTeamId, setLocationLoadingTeamId] = useState(null);

  // Fetch team status
  const { data: teamStatus, isLoading, error, refetch } = useQuery({
    queryKey: ['teams', 'status', selectedDate],
    queryFn: () => api.get(`/teams/status?date=${selectedDate}`).then(res => res.data),
  });

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleRefresh = () => {
    refetch();
    toast({
      title: "Yenilendi",
      description: "Ekip durumu güncellendi.",
    });
  };

  const handleShowLocation = async (team) => {
    if (!team.gps_device_no) {
      toast({
        title: 'GPS Cihazı Tanımsız',
        description: 'Bu ekip için Arvento GPS cihaz numarası tanımlı değil.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSelectedTeam(team);
      setLocationData(null);
      setLocationLoadingTeamId(team.team_id);
      setIsLocationModalOpen(true);

      const url = endpoints.arvento?.teamLocation
        ? endpoints.arvento.teamLocation(team.team_id)
        : `/arvento/team-location/${team.team_id}`;

      const response = await api.get(url);
      setLocationData(response.data?.data || null);
    } catch (error) {
      console.error('Ekip konumu alınırken hata:', error);
      const message = error.response?.data?.error || 'Araç konumu alınamadı. Lütfen daha sonra tekrar deneyin.';
      toast({
        title: 'Konum Hatası',
        description: message,
        variant: 'destructive',
      });
      setIsLocationModalOpen(false);
    } finally {
      setLocationLoadingTeamId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-48 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-red-700">Veri Yüklenirken Hata Oluştu</h3>
              <p className="text-red-600 mt-2">{error.message}</p>
              <Button 
                onClick={() => window.location.reload()} 
                className="mt-4"
                variant="outline"
              >
                Yeniden Dene
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight mb-2">Ekip Durumu</h1>
        <p className="text-muted-foreground">
          Ekiplerin mevcut durumu ve aktif vakaları
        </p>
      </div>

      {/* Date Selection */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <Activity className="h-5 w-5 mr-2" />
              Tarih Seçimi
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
            >
              <Activity className="h-4 w-4 mr-2" />
              Yenile
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4">
            <div className="space-y-2">
              <Label htmlFor="date">Tarih</Label>
              <Input
                id="date"
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                className="w-auto"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {teamStatus && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="text-center p-6">
              <div className="text-3xl font-bold text-gray-800 mb-2">
                {teamStatus.summary?.total || 0}
              </div>
              <div className="text-sm text-gray-600">Toplam Ekip</div>
            </CardContent>
          </Card>
          
          <Card className="border-green-200 bg-green-50">
            <CardContent className="text-center p-6">
              <div className="text-3xl font-bold text-green-800 mb-2">
                {teamStatus.summary?.available || 0}
              </div>
              <div className="text-sm text-green-600">Müsait</div>
            </CardContent>
          </Card>
          
          <Card className="border-red-200 bg-red-50">
            <CardContent className="text-center p-6">
              <div className="text-3xl font-bold text-red-800 mb-2">
                {teamStatus.summary?.busy || 0}
              </div>
              <div className="text-sm text-red-600">Görevde</div>
            </CardContent>
          </Card>
          
          <Card className="border-gray-200 bg-gray-50">
            <CardContent className="text-center p-6">
              <div className="text-3xl font-bold text-gray-800 mb-2">
                {teamStatus.summary?.unassigned || 0}
              </div>
              <div className="text-sm text-gray-600">Atanmamış</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Team Status List */}
      {teamStatus && teamStatus.data && teamStatus.data.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-purple-700">
              <Activity className="h-5 w-5 mr-2" />
              Ekip Detayları ({selectedDate})
            </CardTitle>
            <CardDescription>
              Ekiplerin mevcut durumu ve aktif vakaları
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {sortTeamsByCode(teamStatus.data).map((team) => (
                <div key={team.team_id} className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-4">
                    <Badge 
                      variant={team.status === 'available' ? 'default' : team.status === 'busy' ? 'destructive' : 'secondary'}
                      className="min-w-20 text-center"
                    >
                      {team.status === 'available' ? (
                        <>
                          <UserCheck className="h-3 w-3 mr-1" />
                          Müsait
                        </>
                      ) : team.status === 'busy' ? (
                        <>
                          <Activity className="h-3 w-3 mr-1" />
                          Görevde
                        </>
                      ) : (
                        <>
                          <UserX className="h-3 w-3 mr-1" />
                          Atanmamış
                        </>
                      )}
                    </Badge>
                    
                    <div className="flex items-center space-x-3">
                      <span className="font-medium text-gray-900">{team.team_code}</span>
                      {team.ambulance_plate && (
                        <span className="text-gray-600 text-sm">({team.ambulance_plate})</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    {/* Günlük Toplam Vaka Sayısı */}
                    <div className="text-center px-3 py-1 bg-blue-50 rounded-lg border border-blue-200 min-w-16">
                      <div className="text-lg font-bold text-blue-700">
                        {team.daily_total_cases || 0}
                      </div>
                      <div className="text-xs text-blue-500">
                        Bugün
                      </div>
                    </div>

                    {/* Aktif Durum + Konum */}
                    <div className="flex items-center space-x-3">
                      <div className="text-right min-w-28">
                        {team.active_cases > 0 ? (
                          <div className="space-y-1">
                            <div className="text-red-600 font-medium flex items-center justify-end">
                              <Activity className="h-3 w-3 mr-1" />
                              {team.active_cases} aktif vaka
                            </div>
                            {team.case_details && team.case_details.length > 0 && (
                              <div className="text-xs text-gray-500 max-w-xs text-right">
                                {team.case_details.slice(0, 2).join(', ')}
                                {team.case_details.length > 2 && '...'}
                              </div>
                            )}
                          </div>
                        ) : team.status === 'available' ? (
                          <div className="text-green-600 font-medium flex items-center">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Müsait
                          </div>
                        ) : (
                          <div className="text-gray-500 text-sm">
                            Atanmamış
                          </div>
                        )}
                      </div>

                      <Button
                        variant="outline"
                        size="icon"
                        disabled={locationLoadingTeamId === team.team_id}
                        onClick={() => handleShowLocation(team)}
                        className={!team.gps_device_no ? 'opacity-40 cursor-not-allowed' : ''}
                        title={
                          !team.gps_device_no
                            ? 'Bu ekip için GPS cihaz numarası tanımlı değil'
                            : 'Son araç konumunu görüntüle'
                        }
                      >
                        {locationLoadingTeamId === team.team_id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <MapPin className="h-4 w-4 text-emerald-600" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Ekip Bulunamadı</h3>
              <p className="text-gray-500">
                {selectedDate} tarihi için ekip durumu bulunamadı.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      <TeamLocationModal
        open={isLocationModalOpen}
        onOpenChange={setIsLocationModalOpen}
        team={selectedTeam}
        data={locationData}
        isLoading={!!locationLoadingTeamId}
      />
    </div>
  );
};

export default TeamStatusPage; 